import React, { useState } from 'react';
import { CircuitBreakerState } from '../../types';
import { apiService } from '../../services/api';

interface ResetCircuitBreakerButtonProps {
  queueId: number;
  circuitState?: CircuitBreakerState;
  onSuccess?: (result: any) => void;
}

/**
 * ResetCircuitBreakerButton - Button to reset a tripped circuit breaker
 *
 * Features:
 * - Only visible when circuit state is OPEN
 * - Shows confirmation dialog before reset
 * - Displays success/error feedback
 * - Calls onSuccess callback after successful reset
 */
const ResetCircuitBreakerButton: React.FC<ResetCircuitBreakerButtonProps> = ({
  queueId,
  circuitState,
  onSuccess,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Don't render if circuit state is not OPEN or not enabled
  if (!circuitState || circuitState.state !== 'OPEN' || !circuitState.enabled) {
    return null;
  }

  const handleOpenDialog = () => {
    setShowDialog(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancel = () => {
    setShowDialog(false);
    setError(null);
  };

  const handleConfirmReset = async () => {
    setIsResetting(true);
    setError(null);

    try {
      const result = await apiService.resetCircuitBreaker(queueId);
      setSuccessMessage('Circuit breaker reset successfully');
      setShowDialog(false);
      onSuccess?.(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset circuit breaker';
      setError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const formatTimestamp = (date: string | Date | null): string => {
    if (!date) return 'Unknown';
    const d = new Date(date);
    return d.toISOString().replace('T', ' ').slice(0, 19);
  };

  return (
    <>
      {/* Reset Button - hidden when dialog is open to avoid multiple matching buttons */}
      {!showDialog && (
        <button
          type="button"
          onClick={handleOpenDialog}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          aria-label="Reset Circuit Breaker"
        >
          Reset Circuit Breaker
        </button>
      )}

      {/* Success Message */}
      {successMessage && !showDialog && (
        <div className="mt-2 text-sm text-green-600 dark:text-green-400">
          {successMessage}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="dialog-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-black bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={handleCancel}
            />

            {/* Dialog panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div
              className="inline-block align-bottom bg-white dark:bg-cap-dark rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
            >
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <svg
                    className="h-6 w-6 text-yellow-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-warm-white" id="dialog-title">
                    Reset Circuit Breaker
                  </h3>
                  <div className="mt-2" id="dialog-description">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to reset the circuit breaker? This will allow the queue to resume processing.
                    </p>
                    <div className="mt-4 bg-gray-50 dark:bg-gray-900 rounded-md p-3 text-left">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium">Reason: </span>
                        {circuitState.reason || 'Unknown reason'}
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                        <span className="font-medium">Tripped at: </span>
                        {formatTimestamp(circuitState.opened_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 text-sm text-red-600 dark:text-red-400 text-center">
                  {error}
                </div>
              )}


              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleConfirmReset}
                  disabled={isResetting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResetting ? 'Resetting...' : 'Yes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isResetting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-cap-dark text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResetCircuitBreakerButton;
